import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ClientAssociationGroup } from '@blisspointmedia/bpm-types/dist/ClientAssociations';
import { CustomModalEditWidgetHeader } from '../../Components/ModalEditWidget/modal-edit-constants';
import { useClientAssociationGroups } from './useClientAssociationGroups';

export const PAGE_TITLE = 'Client Associations';
export const PAGE_TYPE = 'Client Associations';
export const CLIENTS_OPTION_KEY = 'clients';

const MAX_SHOWN_CLIENT_IDS = 3;
const MAX_OVERLAY_CLIENT_IDS = 10;

export type GroupRow = ClientAssociationGroup & { id?: string };

export type ClientAssociationsMutations = Pick<
  ReturnType<typeof useClientAssociationGroups>,
  'createGroup' | 'updateGroup' | 'deleteGroup'
>;

export const DEFAULT_NEW_ROW: Partial<GroupRow> = {
  name: '',
  description: '',
  client_ids: []
};

const clientsColumnRenderer = (data: GroupRow) => {
  const clientIds = data.client_ids ?? [];
  if (clientIds.length === 0) {
    return <div className='client-associations-cell-text'>-</div>;
  }

  const sortedClientIds = [...clientIds].sort();

  if (sortedClientIds.length <= MAX_SHOWN_CLIENT_IDS) {
    return <div className='client-associations-cell-text'>{sortedClientIds.join(', ')}</div>;
  }

  const shownClientIds = sortedClientIds.slice(0, MAX_SHOWN_CLIENT_IDS);
  const remainingCount = sortedClientIds.length - shownClientIds.length;
  const overlayShownClientIds = sortedClientIds.slice(0, MAX_OVERLAY_CLIENT_IDS);
  const overlayRemainingCount = sortedClientIds.length - overlayShownClientIds.length;

  return (
    <OverlayTrigger
      placement='right'
      overlay={
        <Tooltip
          id={`client-associations-tooltip-${data.id ?? data.name}`}
          className='client-associations-overlay-trigger'
        >
          <div className='tooltip-text-container'>
            {overlayShownClientIds.map(clientId => (
              <div key={clientId}>{clientId}</div>
            ))}
            {overlayRemainingCount > 0 && (
              <div className='tooltip-item'>{`+ ${overlayRemainingCount} more`}</div>
            )}
          </div>
        </Tooltip>
      }
    >
      <div className='client-associations-cell-text'>
        {`${shownClientIds.join(', ')} + ${remainingCount} more`}
      </div>
    </OverlayTrigger>
  );
};

export const HEADERS: CustomModalEditWidgetHeader[] = [
  {
    label: 'Client Group Name',
    field: 'name',
    type: 'text',
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (value: string) => !value || value.trim().length === 0
  },
  {
    label: 'Clients',
    field: 'client_ids',
    type: 'select',
    options: CLIENTS_OPTION_KEY,
    isMulti: true,
    searchable: true,
    flex: 4,
    modalRow: 1,
    modalFlex: 1,
    renderer: clientsColumnRenderer,
    isInvalid: (value: string[] | undefined) => !value || value.length === 0
  },
  {
    label: 'Description',
    field: 'description',
    type: 'richText',
    flex: 4,
    modalRow: 2,
    modalFlex: 1
  },
  {
    label: 'Updated By',
    field: 'updated_by',
    type: 'text',
    flex: 2,
    modalRow: 3,
    modalFlex: 1,
    uneditable: true
  },
  {
    label: 'Updated At',
    field: 'updated_at',
    type: 'timestamp',
    flex: 2,
    modalRow: 3,
    modalFlex: 1,
    uneditable: true
  }
];
